/**
 * Voegt een CutContour-snijcontour toe aan een al gegenereerd printbestand.
 *
 * DE CONVENTIE
 * ------------
 * Print.com (en de drukwereld in het algemeen) herkent een snijcontour als
 * een gesloten vectorpad in een steunkleur (spot color) die exact "CutContour"
 * heet, alleen een lijn, geen vulling. Dat staat met zoveel woorden op onze
 * eigen bestandseisen-pagina (`src/app/[locale]/bestandseisen/page.tsx`,
 * `cutlineIntro`/`cutlineSteps`) en is precies wat de vinylkern al implementeert
 * voor een andere exportstroom (de vinylkern, destijds gevendord als
 * `src/lib/vinyl/vinyl-core.esm.js` en inmiddels vervangen door de Vinyl Lettering
 * Designer; `CUT_SEPARATION = "CutContour"`). Deze module past diezelfde conventie toe
 * op het rasterbestand dat `design-editor-modal.tsx` met jsPDF opbouwt.
 *
 * WAAROM NIET VIA DE PUBLIEKE JSPDF-API
 * --------------------------------------
 * jsPDF kent alleen DeviceRGB/DeviceCMYK/DeviceGray als kleurruimte via zijn
 * publieke API. Een losstaande steunkleur (een `/Separation`-kleurruimte met
 * een eigen naam) bestaat niet in die API, en de interne resource-dictionary
 * (`putResourceDictionary` in jsPDF's bron) heeft geen pluginhaak voor een
 * `/ColorSpace`-sleutel — wel voor fonts, patterns, ExtGState en XObjects. Een
 * geldig PDF-bestand staat het WEL toe om na afloop nieuwe objecten toe te
 * voegen zonder de bestaande inhoud aan te raken: een "incremental update"
 * (ISO 32000-1 7.5.6), het standaardmechanisme waarmee bijvoorbeeld een
 * handtekening of formulierinvulling aan een bestaand PDF-bestand wordt
 * toegevoegd. Deze module bouwt zo'n incrementele update: hij voegt een
 * kleine extra contentstream toe (de snijlijn), een nieuwe resource-dictionary
 * die zowel het bestaande plaatje als de nieuwe steunkleur kent, en werkt het
 * paginaobject bij om naar die twee te verwijzen. De oorspronkelijke bytes
 * (inclusief het gecomprimeerde beeld) blijven ongewijzigd staan.
 *
 * Geverifieerd tegen de daadwerkelijke output van jsPDF 3.0.3 (`compress:
 * true`, een enkele `addImage`-aanroep): de Page-, Pages-, Resources- en
 * Catalog-objecten zijn altijd platte tekst (geen stream, dus nooit
 * gecomprimeerd); alleen de contentstream en het beeld zelf kunnen binair
 * zijn. Deze module leest en herschrijft uitsluitend de platte-tekstobjecten
 * en raakt de binaire streams nooit aan.
 */

export interface CutContourBox {
  /** In dezelfde eenheid als de andere velden (hier: mm, top-left-origin). */
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface AppendCutContourOptions {
  /** Volledige paginahoogte in mm, inclusief afloop (voor de Y-flip naar PDF-ruimte). */
  pageHeightMm: number;
  /** Contour, in dezelfde box-ruimte als pageHeightMm (top-left-origin, Y naar beneden). */
  ellipse?: {
    cx: number;
    cy: number;
    rx: number;
    ry: number;
  };
  /**
   * Vrije contour: gesloten paden in mm (top-left-origin), bijvoorbeeld de
   * snijlijn rond een ontwerp uit `src/lib/editor/contour-cut.ts`.
   */
  paths?: Array<Array<{ x: number; y: number }>>;
  /**
   * Rechthoek met afgeronde hoeken (vormpreset "Afgeronde hoeken"), in mm met
   * linksboven-oorsprong; `radius` is al begrensd (zie getRoundedRectParams).
   */
  roundedRect?: { left: number; top: number; width: number; height: number; radius: number };
  /**
   * Naam van de steunkleur. Leveranciers lezen de naam letterlijk; zie
   * `cutContourSeparationName` (Probo: "Cutcontour", anderen: "CutContour").
   */
  separationName?: string;
  /**
   * Linkeronderhoek van de MediaBox in pt, voor bestanden waarvan de
   * MediaBox niet in (0, 0) begint (klantbestanden). Standaard (0, 0).
   */
  originPt?: { x: number; y: number };
}

/**
 * Steunkleurnaam van de snijlijn per leverancier. Probo schrijft hem in zijn
 * eigen bestanden als "Cutcontour" (zie `src/lib/studio/boog-pdf.ts`) en zijn
 * aanleverspecificatie noemt die naam; Print.com en de rest gebruiken
 * "CutContour".
 */
export function cutContourSeparationName(providerType?: string | null): string {
  return String(providerType || '').toUpperCase() === 'PROBO' ? 'Cutcontour' : 'CutContour';
}

/**
 * Steunkleurnaam voor de snijlijn van een vormpreset ("Rond", "Afgeronde
 * hoeken") op `custom_shape_simple`. Print.com verwerkt die doorsnijding op
 * plaatmateriaal alleen met een steunkleur die exact "Thrucut" heet (bevestigd
 * bij een handmatig gecorrigeerde Forex-order van 16 oktober 2026). Probo
 * houdt zijn eigen naam; de rest valt terug op de gewone CutContour-naam.
 * Een productspecifieke `__cutContourName` gaat in de editor altijd voor.
 */
export function shapePresetCutSeparationName(providerType?: string | null): string {
  const provider = String(providerType || '').toUpperCase();
  if (provider === 'PRINTCOM') return 'Thrucut';
  return cutContourSeparationName(provider);
}

const MM_TO_PT = 72 / 25.4;
const DEFAULT_CUT_SEPARATION_NAME = 'CutContour';
// Zelfde tintfunctie als de vinylkern: 0% tint is geen inkt, 100% is vol rood
// in CMYK. Zo blijft de lijn zichtbaar in een platte weergave, terwijl een RIP
// hem op de naam "CutContour" herkent en niet meedrukt.
export const TINT_TRANSFORM = '<< /FunctionType 2 /Domain [0 1] /C0 [0 0 0 0] /C1 [0 1 1 0] /N 1 >>';
// Vooraf gedeeld door MM_TO_PT: de contentstream hieronder schaalt zelf al met
// MM_TO_PT (zie buildCmMatrix), dus zonder deze deling komt de lijn ongeveer
// 2,8x te dik uit. Zelfde constante en zelfde reden als CUT_LINE_WIDTH_MM in
// de vroegere vinylkern (`vinyl-core.esm.js`, verwijderd met de oude ontwerptool).
const CUT_LINE_WIDTH_MM = 0.25 / MM_TO_PT;
// Bezier-benadering van een kwart cirkelboog (4-punts ellipsbenadering),
// nauwkeurig tot ruim binnen wat een snijmachine kan volgen.
const BEZIER_KAPPA = 0.5522847498307936;

function fmt(value: number): string {
  return value.toFixed(3);
}

function decodeLatin1(bytes: Uint8Array): string {
  return new TextDecoder('latin1').decode(bytes);
}

function encodeAscii(text: string): Uint8Array {
  return new TextEncoder().encode(text);
}

function concatBytes(chunks: Uint8Array[]): Uint8Array {
  const total = chunks.reduce((sum, chunk) => sum + chunk.length, 0);
  const result = new Uint8Array(total);
  let offset = 0;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }
  return result;
}

function fail(reason: string): never {
  throw new Error(`CutContour kon niet aan het printbestand worden toegevoegd: ${reason}.`);
}

function extractObjectBody(text: string, objNum: number, label: string): string {
  const pattern = new RegExp(`(?:^|[^0-9])${objNum} 0 obj\\r?\\n<<([\\s\\S]*?)>>\\r?\\nendobj`);
  const match = text.match(pattern);
  if (!match) fail(`object ${objNum} (${label}) is niet gevonden`);
  return match[1];
}

function extractRef(body: string, key: string, label: string): number {
  const match = body.match(new RegExp(`/${key}\\s+(\\d+)\\s+0\\s+R`));
  if (!match) fail(`de /${key}-verwijzing ontbreekt (${label})`);
  return Number(match[1]);
}

/**
 * Bouwt de contentstream-operatoren voor een gevulde ellips als contourpad,
 * in mm-ruimte met linksboven-oorsprong (zelfde conventie als de
 * hulplijn-box in de editor). De aanroepende cm-matrix (zie
 * `appendCutContourLayer`) zet dit om naar PDF-apparaatruimte.
 */
function buildEllipsePathOps(ellipse: NonNullable<AppendCutContourOptions['ellipse']>): string {
  const { cx, cy, rx, ry } = ellipse;
  const kx = rx * BEZIER_KAPPA;
  const ky = ry * BEZIER_KAPPA;
  const right = { x: cx + rx, y: cy };
  const bottom = { x: cx, y: cy + ry };
  const left = { x: cx - rx, y: cy };
  const top = { x: cx, y: cy - ry };

  return [
    `${fmt(right.x)} ${fmt(right.y)} m`,
    `${fmt(right.x)} ${fmt(cy - ky)} ${fmt(cx + kx)} ${fmt(top.y)} ${fmt(top.x)} ${fmt(top.y)} c`,
    `${fmt(cx - kx)} ${fmt(top.y)} ${fmt(left.x)} ${fmt(cy - ky)} ${fmt(left.x)} ${fmt(left.y)} c`,
    `${fmt(left.x)} ${fmt(cy + ky)} ${fmt(cx - kx)} ${fmt(bottom.y)} ${fmt(bottom.x)} ${fmt(bottom.y)} c`,
    `${fmt(cx + kx)} ${fmt(bottom.y)} ${fmt(right.x)} ${fmt(cy + ky)} ${fmt(right.x)} ${fmt(right.y)} c`,
    'h',
  ].join('\n');
}

function buildRoundedRectPathOps(rect: NonNullable<AppendCutContourOptions['roundedRect']>): string {
  const { left, top, width, height } = rect;
  const r = Math.max(0, Math.min(rect.radius, width / 2, height / 2));
  const right = left + width;
  const bottom = top + height;
  if (r === 0) {
    return [`${fmt(left)} ${fmt(top)} m`, `${fmt(right)} ${fmt(top)} l`, `${fmt(right)} ${fmt(bottom)} l`,
      `${fmt(left)} ${fmt(bottom)} l`, 'h'].join('\n');
  }
  const k = r * BEZIER_KAPPA;
  return [
    `${fmt(left + r)} ${fmt(top)} m`,
    `${fmt(right - r)} ${fmt(top)} l`,
    `${fmt(right - r + k)} ${fmt(top)} ${fmt(right)} ${fmt(top + r - k)} ${fmt(right)} ${fmt(top + r)} c`,
    `${fmt(right)} ${fmt(bottom - r)} l`,
    `${fmt(right)} ${fmt(bottom - r + k)} ${fmt(right - r + k)} ${fmt(bottom)} ${fmt(right - r)} ${fmt(bottom)} c`,
    `${fmt(left + r)} ${fmt(bottom)} l`,
    `${fmt(left + r - k)} ${fmt(bottom)} ${fmt(left)} ${fmt(bottom - r + k)} ${fmt(left)} ${fmt(bottom - r)} c`,
    `${fmt(left)} ${fmt(top + r)} l`,
    `${fmt(left)} ${fmt(top + r - k)} ${fmt(left + r - k)} ${fmt(top)} ${fmt(left + r)} ${fmt(top)} c`,
    'h',
  ].join('\n');
}

function buildPolygonPathOps(paths: NonNullable<AppendCutContourOptions['paths']>): string {
  return paths
    .filter((path) => path.length >= 3)
    .map((path) => [
      `${fmt(path[0].x)} ${fmt(path[0].y)} m`,
      ...path.slice(1).map((point) => `${fmt(point.x)} ${fmt(point.y)} l`),
      'h',
    ].join('\n'))
    .join('\n');
}

export function separationNameOf(options: AppendCutContourOptions): string {
  const name = options.separationName || DEFAULT_CUT_SEPARATION_NAME;
  // Een kale PDF-naam, zonder escapes: alleen letters en cijfers.
  if (!/^[A-Za-z][A-Za-z0-9]*$/.test(name)) fail(`ongeldige steunkleurnaam "${name}"`);
  return name;
}

export function buildCutContourContentStream(options: AppendCutContourOptions): string {
  const scale = fmt(MM_TO_PT);
  const originX = fmt(options.originPt?.x ?? 0);
  const flip = fmt(options.pageHeightMm * MM_TO_PT + (options.originPt?.y ?? 0));
  const pathOps = [
    options.ellipse ? buildEllipsePathOps(options.ellipse) : '',
    options.roundedRect ? buildRoundedRectPathOps(options.roundedRect) : '',
    options.paths?.length ? buildPolygonPathOps(options.paths) : '',
  ].filter(Boolean).join('\n');
  if (!pathOps) fail('er is geen contour opgegeven');
  return [
    'q',
    `${scale} 0 0 -${scale} ${originX} ${flip} cm`,
    '/GSCutContour gs',
    `/${separationNameOf(options)} CS`,
    '1 SCN',
    `${fmt(CUT_LINE_WIDTH_MM)} w`,
    '0 j',
    '0 J',
    pathOps,
    'S',
    'Q',
  ].join('\n');
}

function xrefEntry(offset: number): string {
  return `${String(Math.round(offset)).padStart(10, '0')} 00000 n \n`;
}

/**
 * Voegt een CutContour-snijlijn toe aan een door jsPDF gegenereerd
 * enkele-pagina PDF-bestand (zoals `createPrintPdf` in
 * `design-editor-modal.tsx` oplevert: een pagina met precies één
 * `addImage`-plaatsing). Geeft de oorspronkelijke bytes ongewijzigd terug
 * wanneer er geen contour is opgegeven.
 *
 * Gooit een fout (in plaats van stil een kapot bestand te leveren) zodra de
 * structuur niet is wat hier wordt aangenomen: liever een zichtbare fout bij
 * het opslaan dan een printbestand dat Print.com afwijst of dat een
 * niet-passende contour meestuurt.
 */
export function appendCutContourLayer(pdfBytes: Uint8Array, options: AppendCutContourOptions): Uint8Array {
  const text = decodeLatin1(pdfBytes);

  const startxrefMatch = text.match(/startxref\s*(\d+)\s*%%EOF\s*$/);
  if (!startxrefMatch) fail('de startxref van het bronbestand is niet gevonden');
  const originalStartxref = Number(startxrefMatch[1]);

  const trailerMatch = text.match(/trailer\s*<<([\s\S]*?)>>\s*startxref/);
  if (!trailerMatch) fail('de trailer van het bronbestand is niet gevonden');
  const trailerBody = trailerMatch[1];

  const rootMatch = trailerBody.match(/\/Root\s+(\d+)\s+0\s+R/);
  if (!rootMatch) fail('de /Root-verwijzing in de trailer ontbreekt');
  const rootNum = Number(rootMatch[1]);

  const sizeMatch = trailerBody.match(/\/Size\s+(\d+)/);
  if (!sizeMatch) fail('de /Size in de trailer ontbreekt');
  // /Size is het hoogste objectnummer + 1, dus meteen het eerstvolgende vrije
  // nummer. Nieuwe objecten hierop laten aansluiten voorkomt een botsing met
  // een bestaand object, zonder de hele bytestream te hoeven doorzoeken.
  const nextObjNum = Number(sizeMatch[1]);

  const catalogBody = extractObjectBody(text, rootNum, 'Catalog');
  const pagesNum = extractRef(catalogBody, 'Pages', 'Catalog');

  const pagesBody = extractObjectBody(text, pagesNum, 'Pages');
  const kidsMatch = pagesBody.match(/\/Kids\s*\[\s*(\d+)\s+0\s+R/);
  if (!kidsMatch) fail('de /Kids-lijst van het Pages-object ontbreekt');
  const pageNum = Number(kidsMatch[1]);

  const pageBody = extractObjectBody(text, pageNum, 'Page');
  const oldResNum = extractRef(pageBody, 'Resources', 'Page');
  const contentsRefMatch = pageBody.match(/\/Contents\s+(\d+)\s+0\s+R/);
  if (!contentsRefMatch) fail('de /Contents-verwijzing van de pagina ontbreekt of is al een lijst');
  const oldContentsNum = Number(contentsRefMatch[1]);

  const oldResBody = extractObjectBody(text, oldResNum, 'Resources');
  if (/\/ColorSpace\b/.test(oldResBody) || /\/ExtGState\b/.test(oldResBody)) {
    // Vandaag heeft createPrintPdf altijd precies een addImage-aanroep en dus
    // nooit een eigen ColorSpace/ExtGState. Verandert dat, dan zou een blinde
    // aanvulling een dubbele sleutel opleveren; beter stoppen dan een
    // twijfelachtig bestand versturen.
    fail('de resources bevatten al een /ColorSpace of /ExtGState, aanvullen zou een dubbele sleutel geven');
  }

  const tintFuncNum = nextObjNum;
  const extGStateNum = nextObjNum + 1;
  const cutContentNum = nextObjNum + 2;
  const newResNum = nextObjNum + 3;

  const objects: Array<{ num: number; body: string }> = [];

  objects.push({
    num: tintFuncNum,
    body: `${tintFuncNum} 0 obj\n${TINT_TRANSFORM}\nendobj\n`,
  });

  objects.push({
    num: extGStateNum,
    body: `${extGStateNum} 0 obj\n<< /Type /ExtGState /OP true /op true /OPM 1 >>\nendobj\n`,
  });

  const contentStream = buildCutContourContentStream(options);
  const contentBytes = encodeAscii(contentStream);
  objects.push({
    num: cutContentNum,
    body: `${cutContentNum} 0 obj\n<< /Length ${contentBytes.length} >>\nstream\n${contentStream}\nendstream\nendobj\n`,
  });

  const separationName = separationNameOf(options);
  const newResBody = `${oldResBody}\n/ColorSpace << /${separationName} [/Separation /${separationName} /DeviceCMYK ${tintFuncNum} 0 R] >>\n/ExtGState << /GSCutContour ${extGStateNum} 0 R >>\n`;
  objects.push({
    num: newResNum,
    body: `${newResNum} 0 obj\n<<${newResBody}>>\nendobj\n`,
  });

  const newPageBody = pageBody
    .replace(`/Resources ${oldResNum} 0 R`, `/Resources ${newResNum} 0 R`)
    .replace(`/Contents ${oldContentsNum} 0 R`, `/Contents [${oldContentsNum} 0 R ${cutContentNum} 0 R]`);
  const updatedPageObject = { num: pageNum, body: `${pageNum} 0 obj\n<<${newPageBody}>>\nendobj\n` };

  // Bytes opbouwen en offsets bijhouden voor de nieuwe xref-tabel. De
  // paginaoverschrijving staat vooraan (bestaand, lager objectnummer), de
  // nieuwe objecten erna, aflopend qua volgorde van aanmaken.
  const appended: Array<{ num: number; bytes: Uint8Array }> = [
    { num: updatedPageObject.num, bytes: encodeAscii(updatedPageObject.body) },
    ...objects.map((obj) => ({ num: obj.num, bytes: encodeAscii(obj.body) })),
  ];

  const originalBytes = pdfBytes;
  let runningOffset = originalBytes.length;
  const offsetsByNum = new Map<number, number>();
  for (const entry of appended) {
    offsetsByNum.set(entry.num, runningOffset);
    runningOffset += entry.bytes.length;
  }

  const newXrefOffset = runningOffset;
  const highestObjNum = newResNum;
  // Each xref entry is exactly 20 bytes including its own end of line
  // (ISO 32000-1 7.5.4), so entries are concatenated, not joined: an extra
  // newline between entries made strict readers (mupdf) fall back to repairing
  // the file, and the repaired file kept the old page without the cut line.
  const xrefLines = 'xref\n'
    + `${pageNum} 1\n`
    + xrefEntry(offsetsByNum.get(pageNum)!)
    + `${tintFuncNum} ${highestObjNum - tintFuncNum + 1}\n`
    + [tintFuncNum, extGStateNum, cutContentNum, newResNum].map((num) => xrefEntry(offsetsByNum.get(num)!)).join('');

  const newTrailer = [
    'trailer',
    `<< /Size ${highestObjNum + 1} /Root ${rootNum} 0 R /Prev ${originalStartxref} >>`,
    'startxref',
    String(newXrefOffset),
    '%%EOF',
    '',
  ].join('\n');

  return concatBytes([
    originalBytes,
    ...appended.map((entry) => entry.bytes),
    encodeAscii(xrefLines),
    encodeAscii(newTrailer),
  ]);
}

function base64FromBytes(bytes: Uint8Array): string {
  const chunkSize = 0x8000;
  let binary = '';
  for (let offset = 0; offset < bytes.length; offset += chunkSize) {
    const chunk = bytes.subarray(offset, offset + chunkSize);
    binary += String.fromCharCode(...Array.from(chunk));
  }
  return btoa(binary);
}

/**
 * Patcht een jsPDF-datauristring (zoals `pdf.output('datauristring')`
 * teruggeeft) met een CutContour-laag en levert weer een datauristring op.
 * Geeft de invoer ongewijzigd terug als het geen `data:application/pdf` is.
 */
export function appendCutContourLayerToDataUrl(dataUrl: string, options: AppendCutContourOptions): string {
  const marker = ';base64,';
  const markerIndex = dataUrl.indexOf(marker);
  if (!dataUrl.startsWith('data:application/pdf') || markerIndex === -1) return dataUrl;

  const base64 = dataUrl.slice(markerIndex + marker.length);
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);

  const patched = appendCutContourLayer(bytes, options);
  const prefix = dataUrl.slice(0, markerIndex + marker.length);
  return `${prefix}${base64FromBytes(patched)}`;
}
